import React, { useState, useEffect, useRef } from 'react'
import { Form, Field, Formik, FormikHandlers } from 'formik'
import Button from 'components/SupportQuestions/Button'
import { Markdown } from 'components/Icons'
import * as Yup from 'yup'
import TextareaAutosize from 'react-textarea-autosize'
import { useUser } from 'hooks/useUser'
import getAvatarURL from '../util/getAvatar'

export const DefaultAvatar = (props: { className?: string; src?: string }) => {
    return (
        <div className={`overflow-hidden rounded-full ${props.className}`}>
            {props.src ? (
                <img className="h-full w-full" alt="" src={props.src} />
            ) : (
                <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M20.0782 41.0392H5.42978C4.03134 41.0392 3.1173 40.1642 3.09386 38.7736C3.07823 37.7814 3.07042 36.797 3.10948 35.8048C3.15636 34.6329 3.72668 33.7345 4.74228 33.1798C8.0782 31.3595 11.4299 29.5783 14.7659 27.7658C15.0081 27.633 15.1565 27.758 15.3362 27.8517C18.1878 29.3439 21.0942 29.4689 24.0626 28.2267C24.1485 28.1955 24.2423 28.1721 24.3126 28.1096C24.9298 27.5861 25.4845 27.7971 26.1251 28.1486C29.1173 29.7971 32.1331 31.4143 35.1487 33.0238C36.4534 33.7191 37.094 34.766 37.0706 36.2426C37.0549 37.0785 37.0706 37.9067 37.0706 38.7426C37.0628 40.1254 36.1409 41.0395 34.7659 41.0395H20.0783L20.0782 41.0392Z"
                        fill="#BFBFBC"
                    />
                    <path
                        d="M19.8359 27.0625C17.0859 26.9687 14.8047 25.6094 13.1251 23.1953C10.3751 19.2344 10.7032 13.6093 13.8516 10.0001C17.2735 6.08599 22.9452 6.10943 26.336 10.0469C29.9376 14.2345 29.711 20.8437 25.8126 24.6405C24.2188 26.1952 22.3126 27.0312 19.8362 27.0624L19.8359 27.0625Z"
                        fill="#BFBFBC"
                    />
                </svg>
            )}
        </div>
    )
}

const fields = {
    avatar: {
        label: 'Avatar',
        component: Avatar,
        hideLabel: true,
        className: 'flex-grow flex items-end',
    },
    firstName: {
        type: 'fname',
        label: 'First name',
        className: 'w-[calc(50%-40px)] grid items-end',
    },
    lastName: {
        type: 'lname',
        label: 'Last name',
        className: 'w-[calc(50%-40px)] grid items-end',
    },
    github: {
        label: 'GitHub',
    },
    linkedin: {
        label: 'LinkedIn',
    },
    biography: {
        component: () => (
            <Field
                minRows={6}
                rows={6}
                as={TextareaAutosize}
                type="text"
                name="biography"
                placeholder="280 characters or less..."
                className="mb-2 w-full rounded-md border border-gray-accent-light py-2 px-4 text-lg dark:text-primary"
            />
        ),
        className: 'w-full',
    },
}

function capitalizeFirstLetter(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1)
}

const ValidationSchema = Yup.object().shape({
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
    website: Yup.string().url('Invalid URL').nullable(),
    github: Yup.string().url('Invalid URL').nullable(),
    linkedin: Yup.string().url('Invalid URL').nullable(),
    twitter: Yup.string().url('Invalid URL').nullable(),
    biography: Yup.string().max(3000, 'Please limit your bio to 3,000 characters, you wordsmith!').nullable(),
})

type EditProfileProps = {
    onSubmit?: (() => void) | (() => Promise<void>)
}

function Avatar({ values, setFieldValue }) {
    const inputRef = useRef()
    const [imageURL, setImageURL] = useState(values?.avatar)

    const handleChange = (e) => {
        const file = e.target.files[0]
        setFieldValue('avatar', file)
        const reader = new FileReader()
        reader.onloadend = () => {
            reader?.result && setImageURL(reader.result)
        }

        reader.readAsDataURL(file)
    }

    useEffect(() => {
        if (!values.avatar && inputRef?.current) {
            inputRef.current.value = null
        }
        setImageURL(values.avatar)
    }, [values.avatar])

    return (
        <div className="group relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-full border  border-gray-accent-light dark:border-gray-accent-dark dark:text-white/50">
            {imageURL ? (
                <img className="absolute inset-0 h-full w-full object-cover" src={imageURL} />
            ) : (
                <DefaultAvatar className="absolute bottom-0 h-[60px] w-[60px]" />
            )}
            <div
                className={`grid ${
                    imageURL ? 'grid-cols-2' : 'grid-cols-1'
                } divide z-10 h-full w-full items-center divide-x divide-dashed divide-gray-accent-light bg-white/90 opacity-0 transition-opacity group-hover:opacity-100 dark:bg-black/80`}
            >
                {imageURL && (
                    <button
                        onClick={(e) => {
                            e.preventDefault()
                            setFieldValue('avatar', null)
                        }}
                        className="group flex h-full w-full items-center justify-center text-4xl"
                    >
                        &#215;
                    </button>
                )}
                <div className="group relative flex h-full w-full items-center justify-center">
                    <span className="text-3xl">&#8593;</span>
                    <input
                        ref={inputRef}
                        onChange={handleChange}
                        accept=".jpg, .png, .gif, .jpeg"
                        className="absolute top-0 left-0 h-full w-full cursor-pointer opacity-0"
                        name="avatar"
                        type="file"
                    />
                </div>
            </div>
        </div>
    )
}

export const EditProfile: React.FC<EditProfileProps> = ({ onSubmit }) => {
    const { user, fetchUser, isLoading, getJwt } = useUser()

    if (!user) return null

    const { firstName, lastName, website, github, linkedin, twitter, biography, id } = user?.profile || {}

    const avatar = getAvatarURL(user?.profile)

    // TODO: Move this logic into the useUser hook
    const handleSubmit = async ({ avatar, ...values }, { setSubmitting }) => {
        setSubmitting(true)
        const JWT = await getJwt()
        let image = avatar
        if (avatar && typeof avatar === 'object') {
            const formData = new FormData()
            formData.append('files', avatar)
            const uploadedImage = await fetch(`${process.env.GATSBY_IDENGAGER_API_HOST}/api/upload`, {
                method: 'post',
                body: formData,
                headers: {
                    Authorization: `Bearer ${JWT}`,
                },
            }).then((res) => res.json())
            if (uploadedImage?.length > 0) {
                image = uploadedImage[0]
            }
        }

        const body = {
            data: {
                ...values,
                ...((image && typeof image !== 'string') || image === null ? { avatar: image?.id ?? null } : {}),
            },
        }

        const { data, error } = await fetch(
            `${process.env.GATSBY_IDENGAGER_API_HOST}/api/profiles/${id}?populate=avatar`,
            {
                method: 'PUT',
                body: JSON.stringify(body),
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${JWT}`,
                },
            }
        ).then((res) => res.json())

        setSubmitting(false)
        if (data) {
            await fetchUser(JWT)
            onSubmit?.()
        }
    }

    return (
        <Formik
            initialValues={{ avatar, firstName, lastName, website, github, linkedin, twitter, biography }}
            validationSchema={ValidationSchema}
            onSubmit={handleSubmit}
        >
            {({ isSubmitting, isValid, values, errors, setFieldValue }) => {
                return (
                    <Form className="m-0">
                        <h2>Update profile</h2>
                        <p className="mb-4 rounded-md border border-dashed border-gray-accent-light p-4 dark:border-gray-accent-dark">
                            <strong>Tip:</strong> Be sure to use full URLs when adding links to your website, GitHub,
                            LinkedIn and Twitter (start with https)
                        </p>
                        <div className="m-0 flex flex-wrap">
                            {Object.keys(values).map((key) => {
                                const error = errors[key]
                                const field = fields[key]
                                const label = field?.label || capitalizeFirstLetter(key.replaceAll('_', ' '))

                                return (
                                    <div className={`${field?.className ?? 'w-1/2'} p-2`} key={key}>
                                        {!field?.hideLabel && <label htmlFor={key}>{label}</label>}
                                        {(field?.component && field.component({ values, setFieldValue })) || (
                                            <Field
                                                className="m-0 w-full rounded-md border border-gray-accent-light py-2 px-4 text-lg dark:text-primary"
                                                type={field?.type || 'text'}
                                                name={key}
                                                placeholder={label}
                                            />
                                        )}
                                        {error && (
                                            <span className="my-1 inline-block font-semibold text-red">{error}</span>
                                        )}
                                    </div>
                                )
                            })}
                        </div>

                        <p className=" mb-4 flex items-center space-x-2 text-sm">
                            <Markdown />
                            <span>Markdown is allowed - even encouraged!</span>
                        </p>

                        <Button loading={isLoading} disabled={isSubmitting || !isValid} type="submit">
                            Update
                        </Button>
                    </Form>
                )
            }}
        </Formik>
    )
}
